import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, errorMessage } from '../lib/api';
import type { Branding } from '../lib/types';
import { useAuth } from '../store/auth';
import { useWorkspace } from '../store/workspace';
import { readableOn } from '../lib/contrast';
import { PageHeader } from '../components/PageHeader';
import { Field } from '../components/Field';
import { Banner } from '../components/Banner';

type BrandSlot = 'logoAssetId' | 'faviconAssetId';

const SLOTS: { key: BrandSlot; kind: string; label: string; hint: string }[] = [
  {
    key: 'logoAssetId',
    kind: 'BRAND_LOGO',
    label: 'Logo',
    hint: 'Shown in the sidebar, on your students’ screens, and faintly under the sign-in form. PNG or SVG on a transparent background.',
  },
  {
    key: 'faviconAssetId',
    kind: 'BRAND_FAVICON',
    label: 'Favicon',
    hint: 'The browser tab icon. A square image, 64×64 or larger.',
  },
];

export function BrandingPage() {
  const { user, organization, setOrganization } = useAuth();
  const workspaceOrgId = useWorkspace((w) => w.organizationId);
  const chooseCentre = useWorkspace((w) => w.choose);
  const [draft, setDraft] = useState<Branding | null>(organization);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const fileInputs = useRef<Record<string, HTMLInputElement | null>>({});

  // A teacher edits their own centre. A platform administrator belongs to no
  // centre at all, so they edit whichever one they have opened — without this
  // the page asked the server to update the organisation `null`.
  const isOwnOrganization = Boolean(user?.organizationId);
  const orgId = user?.organizationId ?? workspaceOrgId;

  useEffect(() => {
    if (isOwnOrganization) {
      setDraft(organization);
      return;
    }
    // The administrator's own session carries no branding to start from, so
    // the opened centre's has to be fetched before it can be edited.
    if (!orgId) return;
    let live = true;
    api
      .get<{ organization: Branding }>(`/organizations/${orgId}`)
      .then(({ data }) => {
        if (live) setDraft(data.organization);
      })
      .catch((err) => {
        if (live) setError(errorMessage(err, 'Could not load this centre'));
      });
    return () => {
      live = false;
    };
  }, [isOwnOrganization, organization, orgId]);

  if (!orgId) {
    return (
      <Banner tone="error">
        Open a centre from Organisations first — appearance belongs to a centre, not to the
        platform.
      </Banner>
    );
  }
  if (!draft) return null;

  async function patch(body: Record<string, unknown>) {
    const { data } = await api.patch<{ organization: Branding }>(`/organizations/${orgId}`, body);
    // Only when it is the administrator's own console being rebranded. Applying
    // a centre's colours to the platform shell would misrepresent where they
    // are working.
    if (isOwnOrganization) setOrganization(data.organization);
    // The rail says which centre is open by name, and it is remembered rather
    // than re-fetched — so renaming one has to say so there too, or the label
    // keeps the old name until the centre is closed and opened again.
    if (workspaceOrgId === data.organization.id) {
      chooseCentre({ id: data.organization.id, name: data.organization.name });
    }
    setDraft(data.organization);
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await patch({
        name: draft!.name,
        primaryColor: draft!.primaryColor,
        accentColor: draft!.accentColor,
        supportEmail: draft!.supportEmail || null,
      });
      setSaved(true);
    } catch (err) {
      setError(errorMessage(err, 'Could not save appearance'));
    } finally {
      setBusy(false);
    }
  }

  async function uploadSlot(slot: BrandSlot, kind: string, file: File) {
    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('kind', kind);
      form.append('file', file);
      const { data } = await api.post<{ asset: { id: string } }>('/assets', form);
      await patch({ [slot]: data.asset.id });
    } catch (err) {
      setError(errorMessage(err, 'Could not upload that image'));
    } finally {
      setBusy(false);
    }
  }

  const urlFor = (slot: BrandSlot) => (slot === 'logoAssetId' ? draft.logoUrl : draft.faviconUrl);

  return (
    <>
      <PageHeader
        title="Appearance"
        meta={
          <p className="muted" style={{ maxWidth: '54ch' }}>
            Your students see this identity everywhere — the sign-in page, the console and the exam
            screen. They never see the platform's own branding.
          </p>
        }
      />

      <div className="stack">
        {error && <Banner tone="error">{error}</Banner>}
        {saved && <Banner tone="success">Appearance saved.</Banner>}

        <form className="panel stack rise" onSubmit={(e) => void save(e)}>
          <h2 className="panel__title">Identity</h2>

          <div className="form-grid">
            <Field
              label="Centre name"
              required
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              hint="What students see everywhere, and how this centre is listed."
            />
            <Field
              label="Support email"
              type="email"
              value={draft.supportEmail ?? ''}
              onChange={(e) => setDraft({ ...draft, supportEmail: e.target.value })}
              hint="Offered to students who cannot sign in."
            />
          </div>

          <div className="form-grid">
            <div className="field">
              <span className="field__label">Primary colour</span>
              <div className="swatch">
                <input
                  type="color"
                  aria-label="Primary colour"
                  value={draft.primaryColor}
                  onChange={(e) => setDraft({ ...draft, primaryColor: e.target.value })}
                />
                <input
                  className="field__input mono"
                  value={draft.primaryColor}
                  onChange={(e) => setDraft({ ...draft, primaryColor: e.target.value })}
                />
              </div>
              <p className="field__hint">
                Used for the navigation spine, buttons and focus rings. Text on top is chosen
                automatically for contrast.
              </p>
            </div>

            <div className="field">
              <span className="field__label">Accent colour</span>
              <div className="swatch">
                <input
                  type="color"
                  aria-label="Accent colour"
                  value={draft.accentColor}
                  onChange={(e) => setDraft({ ...draft, accentColor: e.target.value })}
                />
                <input
                  className="field__input mono"
                  value={draft.accentColor}
                  onChange={(e) => setDraft({ ...draft, accentColor: e.target.value })}
                />
              </div>
              <p className="field__hint">Used sparingly for secondary highlights.</p>
            </div>
          </div>

          {/* Painted from the draft rather than the saved value, so the effect of
              a colour is visible before anything is committed. */}
          <div
            className="row"
            style={{
              background: draft.primaryColor,
              color: readableOn(draft.primaryColor),
              padding: 'var(--s-4)',
              borderRadius: 'var(--radius)',
              justifyContent: 'space-between',
            }}
          >
            <strong style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--step-1)' }}>
              {draft.name || 'Your centre'}
            </strong>
            <span className="mono" style={{ fontSize: '0.6875rem', letterSpacing: '0.14em' }}>
              PREVIEW · CANDIDATE 004182
            </span>
          </div>

          <div className="row">
            <button className="btn btn--primary" type="submit" disabled={busy}>
              {busy ? 'Saving…' : 'Save appearance'}
            </button>
          </div>
        </form>

        <section className="panel stack rise" style={{ animationDelay: '80ms' }}>
          <h2 className="panel__title">Images</h2>
          <div className="form-grid">
            {SLOTS.map((slot) => {
              const url = urlFor(slot.key);
              return (
                <div key={slot.key} className="field">
                  <span className="field__label">{slot.label}</span>
                  <div
                    style={{
                      border: '1px solid var(--rule)',
                      borderRadius: 'var(--radius)',
                      background: 'var(--paper-sunken)',
                      minHeight: '6rem',
                      display: 'grid',
                      placeItems: 'center',
                      padding: 'var(--s-3)',
                    }}
                  >
                    {url ? (
                      <img
                        src={url}
                        alt={slot.label}
                        style={{ maxHeight: '5rem', maxWidth: '100%', objectFit: 'contain' }}
                      />
                    ) : (
                      <span className="muted">Not set</span>
                    )}
                  </div>
                  <p className="field__hint">{slot.hint}</p>
                  <input
                    ref={(el) => {
                      fileInputs.current[slot.key] = el;
                    }}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) void uploadSlot(slot.key, slot.kind, file);
                      e.target.value = '';
                    }}
                  />
                  <div className="row">
                    <button
                      type="button"
                      className="btn"
                      disabled={busy}
                      onClick={() => fileInputs.current[slot.key]?.click()}
                    >
                      {url ? 'Replace' : 'Upload'}
                    </button>
                    {url && (
                      <button
                        type="button"
                        className="btn btn--danger"
                        disabled={busy}
                        onClick={() => void patch({ [slot.key]: null })}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <SafeExamBrowserPanel
          organization={draft}
          busy={busy}
          onSave={(next) => patch(next)}
        />

        <section className="panel rise" style={{ animationDelay: '160ms' }}>
          <h2 className="panel__title">Sign-in link</h2>
          <p className="muted">
            Give your students this address so they land on your branded sign-in page:
          </p>
          <p className="mono" style={{ marginTop: 'var(--s-3)' }}>
            {window.location.origin}/o/{draft.slug}
          </p>
        </section>
      </div>
    </>
  );
}

/**
 * Requiring Safe Exam Browser, and the keys that prove it.
 *
 * The keys are printed by SEB's own configuration tool when a centre saves a
 * .seb file. There are two because they change at different times: the Browser
 * Exam Key whenever SEB itself is updated, the Config Key only when the
 * settings do. A centre may hold either, and more than one of each — which is
 * how a new configuration is rolled out without an outage, since the old key
 * keeps working until every machine has the new file.
 */
function SafeExamBrowserPanel({
  organization,
  busy,
  onSave,
}: {
  organization: Branding;
  busy: boolean;
  onSave: (patch: Record<string, unknown>) => Promise<void>;
}) {
  const [required, setRequired] = useState(Boolean(organization.sebRequired));
  const [skippable, setSkippable] = useState(Boolean(organization.instructionsSkippable));
  const [browserKeys, setBrowserKeys] = useState((organization.sebBrowserExamKeys ?? []).join('\n'));
  const [configKeys, setConfigKeys] = useState((organization.sebConfigKeys ?? []).join('\n'));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const lines = (value: string) =>
    value
      .split(/[\s,]+/)
      .map((line) => line.trim())
      .filter(Boolean);

  async function save() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await onSave({
        sebRequired: required,
        sebBrowserExamKeys: lines(browserKeys),
        sebConfigKeys: lines(configKeys),
        instructionsSkippable: skippable,
      });
      setSaved(true);
    } catch (err) {
      setError(errorMessage(err, 'Could not save those settings'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="panel stack rise" style={{ animationDelay: '140ms' }}>
      <div>
        <h2 className="panel__title">Exam conditions</h2>
        <p className="muted" style={{ maxWidth: '64ch' }}>
          Safe Exam Browser is a free, locked-down browser. It takes the whole screen and blocks
          the things a web page cannot — Alt+F4, Alt+Tab, the Windows key, screen sharing, a second
          monitor. With it required, this platform refuses to open or mark a paper anywhere else.
        </p>
      </div>

      {error && <Banner tone="error">{error}</Banner>}
      {saved && <Banner tone="success">Saved.</Banner>}

      {/*
        * Whether the instruction film has to be watched.
        *
        * Said here, beside the other decision a centre makes about how its
        * sittings are conducted, and worded from the candidate's side: what a
        * teacher is choosing is what the person in the chair is allowed to do.
        */}
      <label className="row" style={{ gap: 'var(--s-2)', alignItems: 'flex-start' }}>
        <input
          type="checkbox"
          checked={skippable}
          onChange={(e) => setSkippable(e.target.checked)}
          style={{ marginTop: '0.2rem' }}
        />
        <span>
          <span style={{ fontWeight: 600 }}>Let candidates skip the instructions</span>
          <br />
          <span className="muted">
            {skippable
              ? 'On. A Skip button appears beside the film and the paper can be started without watching it. Right for students sitting a mock every week, who have seen it before.'
              : 'Off. The film must be watched through before the paper will open — which is what you want the first time somebody meets this screen. The film still plays before the clock starts, so it costs nobody exam time.'}
          </span>
        </span>
      </label>

      <label className="row" style={{ gap: 'var(--s-2)', alignItems: 'flex-start' }}>
        <input
          type="checkbox"
          checked={required}
          onChange={(e) => setRequired(e.target.checked)}
          style={{ marginTop: '0.2rem' }}
        />
        <span>
          <span style={{ fontWeight: 600 }}>Require Safe Exam Browser to sit a paper</span>
          <br />
          <span className="muted">
            Candidates can still sign in and see what they are down to sit. Starting a paper,
            saving an answer and submitting one all need the browser.
          </span>
        </span>
      </label>

      <div className="form-grid">
        <div className="field">
          <label className="field__label" htmlFor="seb-bek">
            Browser Exam Keys
          </label>
          <textarea
            id="seb-bek"
            className="field__input register"
            rows={3}
            spellCheck={false}
            placeholder="One key per line"
            value={browserKeys}
            onChange={(e) => setBrowserKeys(e.target.value)}
          />
          <p className="field__hint">Changes when Safe Exam Browser is updated.</p>
        </div>
        <div className="field">
          <label className="field__label" htmlFor="seb-ck">
            Config Keys
          </label>
          <textarea
            id="seb-ck"
            className="field__input register"
            rows={3}
            spellCheck={false}
            placeholder="One key per line"
            value={configKeys}
            onChange={(e) => setConfigKeys(e.target.value)}
          />
          <p className="field__hint">Changes only when your configuration does.</p>
        </div>
      </div>

      <p className="field__hint" style={{ maxWidth: '64ch' }}>
        Both are printed by SEB Configuration Tool when you save your .seb file — 64 characters of
        0–9 and a–f. Keep at least one from the configuration your machines are running, or nobody
        will be able to start a paper.
      </p>

      <div className="row">
        <button className="btn btn--primary" disabled={busy || saving} onClick={() => void save()}>
          {saving ? 'Saving…' : 'Save exam conditions'}
        </button>
      </div>
    </section>
  );
}
